// 2026-09-22 신규 — 네이버 클립 수익 제출 카드 + 정책 ③ 게이트 조회 (RPC my_naver_revenue_cards · my_naver_revenue_gate).
// 홈 카드(NaverRevenueCard)·수익 페이지 네이버 탭·전면 게이트(NaverRevenueGate)가 같이 쓴다.
// useChannelLimit 과 같은 계약: 모듈 캐시 5분 + 진행 중 요청 합치기(이메일 키). 두 RPC 는 auth.jwt() 이메일로 본인 것만 돌려주므로
// 이메일은 캐시 키·대리 접속 전환 감지용이다.
// refresh() 는 캐시를 비우고 **떠 있는 모든 인스턴스**를 다시 읽게 한다 — 게이트에서 제출하면 홈 카드도 같이 바뀌어야 한다.
// 실패하면 빈 값(gate=false) — 화면을 막지 않는다(서버 my_naver_revenue_gate 가 원본이고, 다음 조회에서 다시 판정).
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { fetchMyCards, fetchMyGate, actionableCards } from "../lib/naverRevenue";

const TTL_MS = 5 * 60 * 1000;
const EMPTY = Object.freeze({ cards: [], gate: { gated: false, cards: [] } });

let cache = null; // { email, at, data: { cards, gate } }
let inflight = null; // { email, promise }
let warnedOnce = false;
const listeners = new Set();

export function _resetNaverRevenueCache() {
  cache = null;
  inflight = null;
  warnedOnce = false;
  listeners.clear();
}

async function fetchAll(email, { force = false } = {}) {
  if (!force && cache && cache.email === email && Date.now() - cache.at < TTL_MS) return cache.data;
  if (inflight && inflight.email === email) return inflight.promise;
  const promise = (async () => {
    const [cards, gate] = await Promise.all([fetchMyCards(supabase), fetchMyGate(supabase)]);
    const data = { cards, gate: { gated: !!gate?.gated, cards: Array.isArray(gate?.cards) ? gate.cards : [] } };
    cache = { email, at: Date.now(), data };
    inflight = null;
    return data;
  })().catch((err) => {
    inflight = null;
    throw err;
  });
  inflight = { email, promise };
  return promise;
}

/** 캐시를 비우고 떠 있는 훅 전부를 다시 읽게 한다 */
export function invalidateNaverRevenueCards() {
  cache = null;
  listeners.forEach((fn) => fn());
}

export function useNaverRevenueCards(email) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(Boolean(email));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    listeners.add(bump);
    return () => listeners.delete(bump);
  }, []);

  useEffect(() => {
    if (!email) {
      setData(EMPTY);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchAll(email, { force: tick > 0 })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!warnedOnce) {
          warnedOnce = true;
          console.warn("네이버 클립 수익 카드 조회 실패:", err?.message || err);
        }
        if (!cancelled) setData(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, tick]);

  const refresh = useCallback(() => invalidateNaverRevenueCards(), []);
  const actionable = useMemo(() => actionableCards(data.cards), [data.cards]);

  return { cards: data.cards, actionable, gate: data.gate, loading, refresh };
}

export default useNaverRevenueCards;
